import { EntityMetaDto } from "@app/api";
import { FieldFormatBaseBool, FieldFormatBaseDate, FieldFormatBaseNumber, FieldFormatBaseString, FieldFormatType, FieldMetadata, ViewMetadata, ViewMetadataUtil } from "@data/cer-data-types";
import { equalsIgnoringCase } from "@util/cer-locale";
import { CerGridComponent } from "src/cer/cer-grid/cer-grid.component";

export type CerDataImportMapSourceType = 'importData' | 'autoGen' | 'parent';

export type CerDataImportMapLinkType = 'none' | 'byName' | 'byText' | 'byParent' | 'byUser' | 'byUserSkip' | 'byNoAccess';

export function linkTypeIcon(linkType: CerDataImportMapLinkType): string {
  switch (linkType) {
    case 'byName':
      return '✅'
    case 'byText':
      return '✅='
    case 'byUser':
      return '✅✍'
    case 'byUserSkip':
      return '⛔✍';
    case 'byParent':
      return '🗲';
    case 'byNoAccess':
      return ''
    default:
      return '❗';
  }
}

export class CerDataImportMapSourceColumn {
  public text?: string;
  public name?: string;
  public origin: CerDataImportMapSourceType;
  public index?: string;
  public dest?: CerDataImportMapDestColumn;
  public linkType: CerDataImportMapLinkType;
}

export type ColumnFormatFn = (v: any) => any;
export class CerDataImportMapActiveColumn {
  public sourceName: string;
  public sourceValue?: any;
  public hasSourceValue: boolean;
  public destName: string;
  public destFKData?: any[];
  public destFKNameField?: string;
  public destFKNameValue?: string;
  public formatFn?: ColumnFormatFn;

  public fkValueGet(value: any): any {
    const fkRow: any = this.destFKData.find(d => d[this.destFKNameValue] == value);
    return fkRow[this.destFKNameField];
  }

  public static formatString(v: string): string {
    if (v == null || v == '') {
      return null;
    }
    else {
      return v as string;
    }
  }

  public static formatNumber(v: any): number {
    if (v === null || v === undefined || v === '') {
      return null;
    }
    else {
      return v as number;
    }
  }

  public static formatBool(v: any): boolean {
    if (v == 0 || v == null || v == '') {
      return false;
    }
    else {
      return v == true;
    }
  }

  public static formatDate(v: any): Date {
    if (v == 0 || v == null || v == '') {
      return null;
    }
    else {
      return new Date((v - (25567 + 2)) * 86400000);
    }
  }
}

export class CerDataImportMapDestColumn {
  constructor(public meta: FieldMetadata, listAllowEdit: boolean, listAllowCreate: boolean) {
    this.allowEdit = listAllowEdit && this.meta.allowEdit !== false;
    this.allowEditOnAdd = listAllowCreate && this.meta.allowEditOnAdd !== false;

    const accessTxt =
      (!this.allowEdit && !this.allowEditOnAdd) ? " 🔒" :
        (this.allowEdit && this.allowEditOnAdd) ? "" :
          (this.allowEdit) ? " (kun rediger)" : " (kun opret)";

    this.name = meta.name;
    this.text = meta.text + accessTxt;
  }

  public name: string;
  public text: string;
  public allowEdit: boolean;
  public allowEditOnAdd: boolean;
}

export class CerDataImportMapDest {
  constructor(public view: ViewMetadata, public allowEdit: boolean, public allowCreate: boolean) {
    this.columns = view.fieldMetaData.map(f => new CerDataImportMapDestColumn(f, this.allowEdit, this.allowCreate));

    this.pkName = ViewMetadataUtil.pkSingleFieldNameGet(view, true);
    this.pk = this.columns.find(c => c.meta.name == this.pkName);
    if (this.pk) {
      this.pk.text = this.pk.meta.text + " 🔑";
    }

    this.parentName = view.parentForeignKey;
    this.parent = this.columns.find(c => c.meta.name == this.parentName);
    if (this.parent) {
      this.parent.text += " 🗲 (fra navigering)";
    }
  }

  public columns: CerDataImportMapDestColumn[];
  public pkName: string;
  public pk: CerDataImportMapDestColumn;
  public parentName: string;
  public parent: CerDataImportMapDestColumn;
}

export class CerDataImportMap {
  constructor(
    public source: CerDataImportMapSourceColumn[],
    public dest: CerDataImportMapDest,
  ) { }

  public status: 'ok' | 'warning' | 'error';
  public statusHtml: string;
}

export class CerDataImportMapper {
  constructor(public map: CerDataImportMap) {
  }

  public update() {
    this.mapColumns();
    this.validate();
    this.sortSource();
  }

  public reset() {
    this.map.source.forEach(s => {
      s.dest = null;
      s.linkType = 'none';
    });
  }

  public mapColumns() {
    const map = this.map;

    const sourceOpen = map.source.filter(s => s.origin == 'importData' && s.linkType != "byUserSkip" && s.linkType != "byUser");

    sourceOpen.forEach(s => {
      // Map on name (priority 1)
      if (s.name.length > 0) {
        s.dest = map.dest.columns.find(d => equalsIgnoringCase(d.meta.name, s.name));
        s.linkType = s.dest != null ? 'byName' : 'none';
      }
      // Map on text (priority 2)
      if (s.dest == null && s.text?.length > 0) {
        s.dest = map.dest.columns.find(d => equalsIgnoringCase(d.meta.text, s.text) || equalsIgnoringCase(d.meta.name, s.text));
        s.linkType = s.dest != null ? 'byText' : 'none';
      }
      // Fallback for no-access
      if (s.dest != null && s.dest.allowEdit == false && s.dest.allowEditOnAdd == false) {
        s.linkType = 'byNoAccess';
      }
    });

    // Link parent (is auto supplied)
    if (map.dest.parent) {
      var parentSources = map.source.filter(s => s.dest == map.dest.parent);
      if (parentSources.length > 1) {
        map.source = map.source.filter(s => s.origin != 'parent');
      }
      else if (parentSources.length == 0) {
        const p = map.dest.parent;
        map.source.push({
          text: p.meta.text ?? "",
          name: p.meta.name,
          origin: 'parent',
          dest: p,
          linkType: 'byParent'
        } as CerDataImportMapSourceColumn);
      };
    }
  }

  public activeMapGet(appGrid: CerGridComponent): CerDataImportMapActiveColumn[] {
    const activeColumns: CerDataImportMapActiveColumn[] = [];
    this.map.source
      .filter(s =>
        (['byName', 'byTitle', 'byUser'].includes(s.linkType) && s.dest != null) ||
        (s.linkType == 'byNoAccess' && s.dest != null && s.dest == this.map.dest.pk) ||
        (s.linkType == 'byParent')
      )
      .forEach(s => {
        const a = new CerDataImportMapActiveColumn();
        a.sourceName = s.origin != 'parent' ? s.text : null;
        a.sourceValue = s.origin == 'parent' ? ViewMetadataUtil.parentValueGet(this.map.dest.view, appGrid.parentDataGet()) : null;
        a.hasSourceValue = s.origin == 'parent';
        a.destName = s.dest.name;

        const destMeta = s.dest.meta;
        if (destMeta != null &&
          destMeta.foreignKeyField?.length > 0 &&
          destMeta.foreignKeyValue?.length > 0 &&
          destMeta.foreignKeyField != destMeta.foreignKeyValue) {

          a.destFKNameField = destMeta.foreignKeyField;
          a.destFKNameValue = destMeta.foreignKeyValue;

          const gridCol = appGrid.grid.getColumnByField(s.dest.name);
          const destFKData = (<any>gridCol.dataSource).dataSource.json;
          a.destFKData = destFKData as any[];
        }
        
        const formats: FieldFormatType[] = Array.isArray(destMeta.format) ? destMeta.format as FieldFormatType[] : [destMeta.format as FieldFormatType];
        const isDate = formats.find(f => FieldFormatBaseDate.includes(f)) != null;
        const isBool = formats.find(f => FieldFormatBaseBool.includes(f)) != null;
        const isNumber = formats.find(f => FieldFormatBaseNumber.includes(f)) != null;
        const isString = formats.find(f => FieldFormatBaseString.includes(f)) != null;
        a.formatFn = isDate   ? CerDataImportMapActiveColumn.formatDate :
                     isBool   ? CerDataImportMapActiveColumn.formatBool :
                     isNumber ? CerDataImportMapActiveColumn.formatNumber : 
                     isString ? CerDataImportMapActiveColumn.formatString : 
                     null;
        activeColumns.push(a);
      });

    return activeColumns;
  }

  public validate() {
    const map = this.map;
    map.status = 'ok';

    const txts: string[] = this.validateRules();

    if (txts.length == 0) {
      map.statusHtml = '✅ Ok';
    } else if (txts.length == 1) {
      map.statusHtml = txts[0];
    }
    else {
      var txt = '';
      txts.forEach(t => {
        txt += `<li>${t}</li>`;
      });
      map.statusHtml = `<ul>${txt}</ul>`;
    }
  }

  private validateRules(): string[] {
    const map = this.map;
    const txts: string[] = [];
    var warning = false;

    // Validate double mapping to destination field
    var destNames = map.source.filter(s => s.dest?.name?.length > 0).map(s => s.dest.name);
    destNames = destNames.filter((dn, idx) => destNames.indexOf(dn) === idx);
    destNames.forEach(name => {
      const sourceForName = map.source.filter(s => s.dest?.name == name);
      if (sourceForName.length > 1) {
        const dub = sourceForName.map(s => `'${s.text}'`).join(", ");
        txts.push(`⛔Kolonner: ${dub}' peger på samme felt`);
        map.status = 'error';
      }
    });

    // Validate access
    if (map.dest.allowCreate == false && map.dest.allowEdit == false) {
      txts.push('⛔Der er ikke adgang til at oprette og redigere');
      map.status = 'error';
    }
    else {
      if (map.dest.allowCreate == false) {
        txts.push('❗Der er ikke adgang til at oprette');
        warning = true;
      }
      //else if (map.dest.pk.pkNamedest.pk && map.source.)
      if (map.dest.allowEdit == false) {
        txts.push('❗Der er ikke adgang til at redigere');
        warning = true;
      }
      else if (map.dest.pk == null) {
        txts.push('❗Poster kan kun oprettes, da der ikke findes en nøgle');
        warning = true;
      }
      else {
        const pkSource = map.source.filter(s => s.dest == map.dest.pk);
        if (pkSource.length == 0) {
          txts.push(`❗Poster kan kun oprettes, da nøglefelt '${map.dest.pk.text}' ikke er forbundet`);
          warning = true;
        }
      }
    }
    const importDataCols = map.source.filter(s => s.origin == 'importData');
    const mappedCount = map.source.filter(s => s.dest != null && s.linkType != 'byParent').length;
    if (mappedCount == 0) {
      txts.push(`⛔Ark har ${importDataCols.length} kolonner, men ingen felter er forbundet`);
      map.status = 'error';
    }
    else {
      txts.push(`✅Ark har ${importDataCols.length} kolonner, hvor ${mappedCount} kan forbundet`);
    }

    if (warning && map.status == 'ok') {
      map.status = 'warning';
    }
    return txts;
  }

  public sortSource() {
    var source = this.map.source;
    var dest = this.map.dest;

    const pk = source.filter(s => s.dest == dest.pk);
    source = source.filter(s => !pk.includes(s));
    const nameLinked = source.filter(s => s.linkType == 'byName');
    source = source.filter(c => !nameLinked.includes(c));
    const textLinked = source.filter(s => s.linkType == 'byText');
    source = source.filter(c => !textLinked.includes(c));
    const userLinked = source.filter(s => s.linkType == 'byUser');
    source = source.filter(c => !userLinked.includes(c));
    const unlinked = source.filter(s => s.linkType === 'none');
    source = source.filter(c => !unlinked.includes(c));
    const noAccess = source.filter(s => s.linkType == 'byNoAccess');
    source = source.filter(c => !noAccess.includes(c));
    const remain = source;

    this.map.source = pk.concat(nameLinked).concat(textLinked).concat(userLinked).concat(unlinked).concat(remain).concat(noAccess);
  }

  public mapApplyByUser() {
    const map = this.map;
    map.source.filter(s => s.dest != null && s.linkType == 'byUserSkip').forEach(s => s.linkType = 'byUser');
    map.source.filter(s => s.dest == null && s.linkType == 'byUser').forEach(s => s.linkType = 'byUserSkip');
  }
}